'use client';
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';

const OurWorkSection = () => {
  const galleryImages = [
    {
      src: '/images/premium_liquor.png',
      alt: 'Ultra-Premium Liquor Private Label',
    },
    {
      src: '/images/luxury_cosmetic.png',
      alt: 'Luxury Skincare Brand Labeling',
    },
    {
      src: '/images/img_picture_178x232.png',
      alt: 'Industrial Custom Labels',
    },
    {
      src: '/images/premium_food_beverage_v2.png',
      alt: 'Premium Food & Beverage Packaging',
    },
  ];

  return (
    <section className="section-shell w-full bg-slate-50 relative overflow-hidden">
      {/* Abstract Background Element */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-50/50 -skew-x-12 transform origin-top-right pointer-events-none" />

      <div className="container-fluid relative z-10">
        <div className="flex flex-col xl:flex-row justify-between items-center gap-fluid-xl">

          {/* Left — Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full xl:max-w-[480px] flex-shrink-0"
          >
            <div className="flex flex-col items-start gap-fluid-md">
              <div className="relative">
                <span className="text-blue-600 font-bold tracking-[3px] uppercase text-xs mb-4 block">Craftsmanship in Every Detail</span>
                <h2 className="type-section text-left text-[#0a1f33] leading-tight">
                  Our Work <br /><span className="italic font-serif opacity-80">in Action</span>
                </h2>
                <div className="w-24 h-1.5 bg-blue-600 mt-6 rounded-full" />
              </div>
              <p className="type-body text-left text-slate-600 leading-relaxed">
                We don&apos;t just print labels; we architect brand experiences. From ultra-premium tactile finishes for spirits to advanced-engineered pharmaceutical solutions, our portfolio is a testament to technical excellence.
              </p>
              <Link href="/portfolio" className="no-tap-enforce group flex items-center gap-[12px] pt-[8px]">
                <span className="type-body font-bold text-left text-[#0a1f33] border-b-2 border-transparent group-hover:border-[#0a1f33] transition-all duration-300 py-1">
                  Explore Full Portfolio
                </span>
                <div className="w-10 h-10 rounded-full bg-[#0a1f33] flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                  <img src="/images/img_arrowright.svg" alt="" aria-hidden="true" className="w-4 h-4 brightness-0 invert" />
                </div>
              </Link>
            </div>
          </motion.div>

          {/* Right — Fluid Image Gallery */}
          <div className="w-full xl:flex-1 min-w-0">

            {/* Mobile & Tablet (below xl:): Simple 2-column grid */}
            <div className="grid xl:hidden grid-cols-2 gap-3 sm:gap-4 w-full">
              {galleryImages.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="bg-white p-1.5 rounded-[16px] border border-slate-200 shadow-xl"
                >
                  {/* Relative wrapper + absolute fill — correct image responsiveness */}
                  <div className="relative w-full aspect-square overflow-hidden rounded-[12px]">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Desktop (xl: and above): Fluid 2-column masonry-style CSS grid */}
            <div className="hidden xl:grid w-full grid-cols-2 grid-rows-[auto_auto] gap-4">
              {/* Feature image — spans 2 rows */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0, duration: 0.7 }}
                whileHover={{ y: -8, scale: 1.01, zIndex: 50 }}
                className="bg-white p-2 rounded-[20px] border border-slate-200 shadow-[0px_24px_48px_rgba(0,0,0,0.10)] cursor-pointer row-span-2"
              >
                {/* Relative wrapper + absolute fill for proper image scaling */}
                <div className="relative w-full overflow-hidden rounded-[14px] min-h-[360px] h-full">
                  <img
                    src={galleryImages[0].src}
                    alt={galleryImages[0].alt}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              </motion.div>

              {/* Right column — 3 smaller cards */}
              {galleryImages.slice(1).map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: (index + 1) * 0.12, duration: 0.7 }}
                  whileHover={{ y: -8, scale: 1.01, zIndex: 50 }}
                  className="bg-white p-2 rounded-[20px] border border-slate-200 shadow-[0px_24px_48px_rgba(0,0,0,0.10)] cursor-pointer"
                >
                  <div className="relative w-full overflow-hidden rounded-[14px] aspect-[16/9]">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default OurWorkSection;